import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';

import type { DraftPreface, LockInfo, ReleaseNotes } from 'views/App/Portal/Policy/_types/manuals/draft';

type NewPrefaceRequest = {
  title: string;
};

export default function useManagePreface() {
  const create = async (
    manualId: string,
    data: NewPrefaceRequest,
    callback?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces`;
    await fetcher({
      url,
      method: 'post',
      body: data,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const update = async (
    manualId: string,
    prefaceId: string,
    data: DraftPreface,
    callback?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}`;
    await fetcher({
      url,
      method: 'put',
      body: data,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updateDraftStatus = async (
    manualId: string,
    prefaceId: string,
    draftState: string,
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}/draft-state`;
    await fetcher({
      url,
      method: 'patch',
      body: { draftState },
    })
      .then(async () => {
        if (callback) await callback();
      })
      .catch((err) => {
        console.warn('update err', err);
        if (onError) handleError(err, { onError });
      });
  };

  const approveChanges = async (
    manualId: string,
    prefaceId: string,
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}/approve-change`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then(async () => {
        if (callback) await callback();
      })
      .catch((err) => {
        console.warn('update err', err);
        if (onError) handleError(err, { onError });
      });
  };

  const getLock = async (
    manualId: string,
    prefaceId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}/lock`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const takeLock = async (
    manualId: string,
    prefaceId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}/lock`;
    await fetcher({
      url,
      method: 'patch',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const removeLock = async (
    manualId: string,
    prefaceId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}/lock`;
    await fetcher({
      url,
      method: 'delete',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updatetGuideSheet = async (
    manualId: string,
    policyId: string,
    guideSheet: {
      title: string;
      content: string;
      contentEditable: string;
    },
    onSuccess?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${policyId}/guide-sheet`;
    await fetcher({
      url,
      method: 'put',
      body: guideSheet,
    })
      .then((data) => {
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const approveGuideSheet = async (
    manualId: string,
    prefaceId: string,
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${prefaceId}/guide-sheet/approve-change`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updateReleaseNotes = async (
    manualId: string,
    policyId: string,
    body: { history: ReleaseNotes['history'] },
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${policyId}/release-notes`;
    await fetcher({
      url,
      method: 'put',
      body,
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const approveReleaseNotes = async (
    manualId: string,
    policyId: string,
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/prefaces/${policyId}/release-notes/approve-change`;
    await fetcher({
      url,
      method: 'post',
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  return {
    approveChanges,
    create,
    getLock,
    removeLock,
    takeLock,
    update,
    updateDraftStatus,
    updatetGuideSheet,
    approveGuideSheet,
    updateReleaseNotes,
    approveReleaseNotes,
  };
}
